import React, { useEffect, useRef, useState, useCallback } from 'react';

/**
 * Fullscreen lightbox for photos and videos.
 *
 * Props:
 *   items       – array of media items
 *   currentIndex – index of currently shown item
 *   onClose     – () => void
 *   onPrev      – () => void
 *   onNext      – () => void
 */

// Prevent right-click context menu (download option)
const preventContextMenu = (e) => {
  e.preventDefault();
};

// Prevent drag-to-save operations
const preventDrag = (e) => {
  e.preventDefault();
};

export default function MediaLightbox({ items = [], currentIndex = 0, onClose, onPrev, onNext }) {
  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);
  const videoRef = useRef(null);
  const [videoStarted, setVideoStarted] = useState(false);

  const item = items[currentIndex];
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex < items.length - 1;
  const isVideo = item?.media_type === 'video';

  useEffect(() => {
    setVideoStarted(false);
  }, [item?.id]);

  const handleStartVideo = useCallback(() => {
    const video = videoRef.current;
    if (!video || !item?.url) return;

    video.src = item.url;
    video.load();
    setVideoStarted(true);

    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise.catch((error) => {
        console.error('Unable to start gallery video playback:', error);
        setVideoStarted(false);
      });
    }
  }, [item?.url]);

  const handleKeyDown = useCallback(
    (e) => {
      switch (e.key) {
        case 'Escape':
          onClose?.();
          break;
        case 'ArrowLeft':
          if (hasPrev) onPrev?.();
          break;
        case 'ArrowRight':
          if (hasNext) onNext?.();
          break;
        default:
          break;
      }
    },
    [onClose, onPrev, onNext, hasPrev, hasNext]
  );

  // Focus trap: focus close button on open
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  if (!item) return null;

  return (
    <div
      className="lightbox-overlay"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose?.();
      }}
      onContextMenu={preventContextMenu}
      onDragStart={preventDrag}
      role="dialog"
      aria-modal="true"
      aria-label={`Media viewer: ${item.name || 'Item ' + (currentIndex + 1)} of ${items.length}`}
    >
      {/* Close */}
      <button
        ref={closeBtnRef}
        type="button"
        className="lightbox-close"
        onClick={onClose}
        aria-label="Close lightbox"
        autoFocus
      >
        &times;
      </button>

      {/* Counter */}
      <div className="lightbox-counter" aria-live="polite">
        {currentIndex + 1} / {items.length}
      </div>

      {/* Prev */}
      {hasPrev && (
        <button
          type="button"
          className="lightbox-nav lightbox-prev"
          onClick={onPrev}
          aria-label="Previous"
        >
          &#8249;
        </button>
      )}

      {/* Content */}
      <div className="lightbox-content">
        {isVideo ? (
          <div className="lightbox-video-wrap">
            <video
              ref={videoRef}
              key={item.id}
              controls={videoStarted}
              controlsList="nodownload"
              className="lightbox-media lightbox-video"
              aria-label={item.name}
            />
            {!videoStarted && (
              <button
                type="button"
                className="lightbox-video-start"
                onClick={handleStartVideo}
                aria-label="Play video"
              >
                <span aria-hidden="true" />
              </button>
            )}
          </div>
        ) : (
          <img
            key={item.id}
            src={item.url}
            alt={item.name || 'Gallery item'}
            className="lightbox-media lightbox-image"
            loading="eager"
            decoding="async"
            draggable={false}
            onContextMenu={preventContextMenu}
            onDragStart={preventDrag}
          />
        )}
        {item.name && (
          <div className="lightbox-caption">{item.name}</div>
        )}
      </div>

      {/* Next */}
      {hasNext && (
        <button
          type="button"
          className="lightbox-nav lightbox-next"
          onClick={onNext}
          aria-label="Next"
        >
          &#8250;
        </button>
      )}
    </div>
  );
}
